import { formatMgrs } from '../shared/grid';
import { statusLabel, type ReportStatus } from '../shared/status';

export function statusPill(status: ReportStatus): HTMLElement {
  const tone =
    status === 'draft'
      ? 'warn'
      : status === 'returned'
        ? 'orange'
        : status === 'pending'
          ? 'danger'
          : status === 'approved'
            ? 'ok'
            : '';
  return el('span', {
    className: tone ? `pill ${tone}` : 'pill',
    textContent: statusLabel(status),
  });
}

export function bylines(report: {
  author_name?: string;
  edited_by?: string[];
  approved_by?: string;
}): HTMLElement[] {
  const lines: HTMLElement[] = [];
  const origin = report.author_name?.trim() || '';
  if (origin) lines.push(el('span', { className: 'byline', textContent: `Originated by ${origin}` }));
  const editors: string[] = [];
  for (const name of report.edited_by || []) {
    const clean = name.trim();
    if (clean && !editors.includes(clean)) editors.push(clean);
  }
  if (editors.length) {
    lines.push(el('span', { className: 'byline', textContent: `Edited by ${editors.join(', ')}` }));
  }
  const approved = report.approved_by?.trim() || '';
  if (approved) lines.push(el('span', { className: 'byline', textContent: `Approved by ${approved}` }));
  return lines;
}

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | boolean | number | undefined> = {},
  children: (Node | string)[] = []
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === false) continue;
    if (k === 'className') node.className = String(v);
    else if (k === 'textContent') node.textContent = String(v);
    else if (k === 'value') (node as HTMLInputElement).value = String(v);
    else if (v === true) node.setAttribute(k, '');
    else node.setAttribute(k, String(v));
  }
  for (const c of children) {
    node.append(typeof c === 'string' ? c : c);
  }
  return node;
}

let statusTimer = 0;

/** One line in the header. A new message replaces the previous one. */
export function toast(message: string, kind: 'ok' | 'err' | 'info' = 'info'): void {
  const node = document.getElementById('status');
  if (!node) return;
  node.hidden = false;
  node.textContent = message;
  node.className = `status ${kind}`;
  window.clearTimeout(statusTimer);
  statusTimer = window.setTimeout(() => {
    node.hidden = true;
  }, 2800);
}

export function field(label: string, control: HTMLElement, hint?: string): HTMLElement {
  const wrap = el('label', { className: 'field' }, [
    el('span', { className: 'field-label', textContent: label }),
    control,
  ]);
  if (hint) wrap.append(el('span', { className: 'field-hint', textContent: hint }));
  return wrap;
}

export function textInput(opts: {
  type?: string;
  placeholder?: string;
  value?: string;
  required?: boolean;
} = {}): HTMLInputElement {
  const input = el('input', {
    type: opts.type || 'text',
    className: 'input',
    placeholder: opts.placeholder || '',
    value: opts.value || '',
    required: opts.required || undefined,
  });
  return input;
}

export function textarea(placeholder = ''): HTMLTextAreaElement {
  return el('textarea', {
    className: 'input',
    placeholder,
    rows: '4',
  });
}

export function selectBox(
  options: { value: string; label: string }[],
  placeholder: string
): HTMLSelectElement {
  const s = el('select', { className: 'input' });
  s.append(el('option', { value: '', textContent: placeholder }));
  for (const o of options) {
    s.append(el('option', { value: o.value, textContent: o.label }));
  }
  return s;
}

export function fmtTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function fromLocalInput(value: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString();
}

export function formatPlace(
  lat: number | null,
  lng: number | null,
  description: string
): string {
  const bits: string[] = [];
  if (lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)) {
    const grid = formatMgrs(lat, lng);
    if (grid) bits.push(grid);
  }
  if (description.trim()) bits.push(description.trim());
  return bits.join(' · ');
}

let navigateListener: () => void = () => {};

export function onNavigate(fn: () => void): void {
  navigateListener = fn;
}

export function go(path: string): void {
  const next = '#' + path.replace(/^#/, '');
  if (location.hash === next) navigateListener();
  else location.hash = next;
}

export function button(
  label: string,
  opts: { primary?: boolean; ghost?: boolean; type?: 'button' | 'submit' } = {}
): HTMLButtonElement {
  return el('button', {
    type: opts.type || 'button',
    className: opts.ghost ? 'btn btn-ghost' : opts.primary === false ? 'btn' : 'btn btn-primary',
    textContent: label,
  });
}
